import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getHelpEntryBySlug, HelpResource } from '../data/helpEntries';
import { Badge } from '../components/ui/Badge';
import { Difficulty } from '../types/lab';

function getDifficultyColor(difficulty: Difficulty) {
  switch (difficulty) {
    case Difficulty.BEGINNER:
      return 'bg-green-500';
    case Difficulty.INTERMEDIATE:
      return 'bg-yellow-500';
    case Difficulty.ADVANCED:
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
}

function formatDifficultyLabel(difficulty: Difficulty) {
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
}

function formatSourceLabel(source: HelpResource['source']) {
  switch (source) {
    case 'digitalocean':
      return 'DigitalOcean';
    case 'official':
      return 'Official';
    default:
      return 'Community';
  }
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
  }).format(date);
}

interface ResourceListProps {
  title: string;
  resources: HelpResource[];
}

interface TextListProps {
  title: string;
  items: string[];
  ordered?: boolean;
}

function TextList({ title, items, ordered = false }: TextListProps) {
  if (!items.length) {
    return null;
  }

  const ListTag = ordered ? 'ol' : 'ul';

  return (
    <section className="mb-8 rounded-lg border border-gray-800 bg-gray-800 p-5">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      <ListTag className={`space-y-3 text-gray-300 leading-relaxed ${ordered ? 'list-decimal pl-5' : ''}`}>
        {items.map(item => (
          <li key={item} className={ordered ? '' : 'flex gap-3'}>
            {!ordered && <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />}
            <span>{item}</span>
          </li>
        ))}
      </ListTag>
    </section>
  );
}

function Concepts({ concepts }: { concepts: string[] }) {
  if (!concepts.length) {
    return null;
  }

  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold mb-3">Concepts To Review</h2>
      <div className="flex flex-wrap gap-2">
        {concepts.map(concept => (
          <span key={concept} className="rounded-md bg-gray-800 px-3 py-1.5 text-sm text-gray-300">
            {concept}
          </span>
        ))}
      </div>
    </section>
  );
}

function ResourceList({ title, resources }: ResourceListProps) {
  if (!resources || resources.length === 0) {
    return null;
  }

  return (
    <section className="mb-10">
      <h2 className="text-2xl font-semibold mb-4">{title}</h2>
      <div className="space-y-4">
        {resources.map(resource => (
          <article key={resource.url} className="rounded-lg border border-gray-800 bg-gray-800 p-5">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <h3 className="text-xl font-semibold text-white">{resource.title}</h3>
              <span className="text-xs uppercase tracking-wide text-gray-400">
                {formatSourceLabel(resource.source)}
              </span>
            </header>
            <p className="text-gray-300 mb-4 leading-relaxed">{resource.summary}</p>
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
              aria-label={`Open ${resource.title} in a new tab`}
            >
              <span>Open documentation</span>
              <span aria-hidden="true">↗</span>
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

export function HelpDetail() {
  const { slug } = useParams<{ slug: string }>();

  const entry = useMemo(() => (slug ? getHelpEntryBySlug(slug) : undefined), [slug]);

  if (!entry) {
    return <Navigate to="/help" replace />;
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen p-6 sm:p-10">
      <div className="max-w-4xl mx-auto">
        <Link to="/help" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-2 mb-6">
          <span aria-hidden="true">←</span>
          <span>Back to Help</span>
        </Link>

        <header className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
            <h1 className="text-4xl font-bold leading-tight">{entry.title}</h1>
            <Badge className={`${getDifficultyColor(entry.difficulty)} text-white`}>
              {formatDifficultyLabel(entry.difficulty)}
            </Badge>
          </div>
          <p className="text-gray-300 text-lg leading-relaxed">{entry.shortDescription}</p>
        </header>

        <div className="mb-8 rounded-lg border border-blue-900/70 bg-blue-950/30 p-5 text-blue-100">
          <h2 className="mb-2 text-xl font-semibold">Learner Support Boundary</h2>
          <p className="leading-relaxed">
            This page is for setup and troubleshooting. Use the lab instructions for exact graded requirements.
          </p>
        </div>

        <TextList title="Before You Start" items={entry.setupNotes} ordered />
        <Concepts concepts={entry.concepts} />
        <TextList title="General Troubleshooting" items={entry.troubleshooting} />
        <ResourceList title="Reference Docs" resources={entry.primaryResources} />
        <ResourceList title="Additional Resources" resources={entry.additionalResources ?? []} />

        <footer className="pt-6 border-t border-gray-800 text-sm text-gray-400">
          <p>
            Last updated: {formatDate(entry.updatedAt)} • Maintained by {entry.maintainer}
          </p>
        </footer>
      </div>
    </div>
  );
}

export default HelpDetail;
